/**
 * Connection and lifecycle mutation helpers for protocol detail.
 */

import { clinicalTrialsService } from "../../api/clinicalTrialsService";
import type {
  ProtocolConnection,
  ProtocolDraftBundle,
  ProtocolTrial,
} from "../../api/clinicalTrialsApiTypes";
import type { ProtocolLifecycleAction } from "./protocolDetailTypes";

type SetError = (value: string | null) => void;
type SetBusy = (value: boolean) => void;

/**
 * Persist a draft connection (suggestion accept or graph drag-connect).
 *
 * @param trialId - Trial identifier
 * @param bundle - Current draft bundle
 * @param connection - Connection payload
 * @param setBusy - Busy-state setter
 * @param setError - Error-state setter
 * @param loadTrial - Reload callback
 * @param confirmMessage - Optional browser confirm prompt; skipped when null
 * @returns Nothing
 */
export async function addDraftConnection(
  trialId: string,
  bundle: ProtocolDraftBundle | null,
  connection: ProtocolConnection,
  setBusy: SetBusy,
  setError: SetError,
  loadTrial: () => Promise<void>,
  confirmMessage: string | null = null,
): Promise<void> {
  if (!bundle) return;
  if (confirmMessage && !window.confirm(confirmMessage)) return;
  setBusy(true);
  setError(null);
  try {
    await clinicalTrialsService.addProtocolConnection(trialId, bundle.draft.draft_id, {
      confirmation: true,
      expected_revision: bundle.draft.revision,
      connection,
    });
    await loadTrial();
  } catch (err) {
    setError(err instanceof Error ? err.message : "Failed to add connection");
  } finally {
    setBusy(false);
  }
}

/**
 * Accept one suggested connection into the draft.
 *
 * @param trialId - Trial identifier
 * @param bundle - Current draft bundle
 * @param connection - Suggested connection
 * @param setBusy - Busy-state setter
 * @param setError - Error-state setter
 * @param loadTrial - Reload callback
 * @returns Nothing
 */
export async function acceptSuggestion(
  trialId: string,
  bundle: ProtocolDraftBundle | null,
  connection: ProtocolConnection,
  setBusy: SetBusy,
  setError: SetError,
  loadTrial: () => Promise<void>,
): Promise<void> {
  await addDraftConnection(
    trialId,
    bundle,
    connection,
    setBusy,
    setError,
    loadTrial,
    `Add connection ${connection.connection_type}?`,
  );
}

/**
 * Remove a draft connection.
 *
 * @param trialId - Trial identifier
 * @param bundle - Current draft bundle
 * @param connectionId - Connection identifier
 * @param setBusy - Busy-state setter
 * @param setError - Error-state setter
 * @param loadTrial - Reload callback
 * @returns Nothing
 */
export async function removeConnection(
  trialId: string,
  bundle: ProtocolDraftBundle | null,
  connectionId: string,
  setBusy: SetBusy,
  setError: SetError,
  loadTrial: () => Promise<void>,
): Promise<void> {
  if (!bundle || !window.confirm("Remove this connection?")) return;
  setBusy(true);
  setError(null);
  try {
    await clinicalTrialsService.deleteProtocolConnection(
      trialId,
      bundle.draft.draft_id,
      connectionId,
      {
        confirmation: true,
        expected_revision: bundle.draft.revision,
      },
    );
    await loadTrial();
  } catch (err) {
    setError(err instanceof Error ? err.message : "Failed to remove connection");
  } finally {
    setBusy(false);
  }
}

/**
 * Run a review/publish lifecycle action.
 *
 * @param trialId - Trial identifier
 * @param bundle - Current draft bundle
 * @param action - Lifecycle action name
 * @param setBusy - Busy-state setter
 * @param setError - Error-state setter
 * @param loadTrial - Reload callback
 * @returns Nothing
 */
export async function runLifecycle(
  trialId: string,
  bundle: ProtocolDraftBundle | null,
  action: ProtocolLifecycleAction,
  setBusy: SetBusy,
  setError: SetError,
  loadTrial: () => Promise<void>,
): Promise<void> {
  if (!bundle) return;
  const comment =
    action === "approve" || action === "request-changes"
      ? window.prompt("Enter a review comment") || ""
      : undefined;
  if ((action === "approve" || action === "request-changes") && !comment?.trim()) {
    return;
  }
  if (!window.confirm(`Confirm ${action.replace("-", " ")}?`)) return;
  setBusy(true);
  setError(null);
  try {
    await clinicalTrialsService.protocolDraftAction(trialId, bundle.draft.draft_id, action, {
      confirmation: true,
      comment,
    });
    await loadTrial();
  } catch (err) {
    setError(err instanceof Error ? err.message : `Failed to ${action}`);
  } finally {
    setBusy(false);
  }
}

/**
 * Archive or restore the current trial.
 *
 * @param trialId - Trial identifier
 * @param trial - Loaded trial metadata
 * @param mode - Archive or restore
 * @param setBusy - Busy-state setter
 * @param setError - Error-state setter
 * @param loadTrial - Reload callback
 * @returns Nothing
 */
export async function archiveToggle(
  trialId: string,
  trial: ProtocolTrial | null,
  mode: "archive" | "restore",
  setBusy: SetBusy,
  setError: SetError,
  loadTrial: () => Promise<void>,
): Promise<void> {
  if (!trial) return;
  if (!window.confirm(`${mode} this trial?`)) return;
  setBusy(true);
  setError(null);
  try {
    const body = { confirmation: true, expected_version: trial.version };
    if (mode === "archive") {
      await clinicalTrialsService.archiveTrial(trialId, body);
    } else {
      await clinicalTrialsService.restoreTrial(trialId, body);
    }
    await loadTrial();
  } catch (err) {
    setError(err instanceof Error ? err.message : `Failed to ${mode} trial`);
  } finally {
    setBusy(false);
  }
}
